import { FC, useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from "react";
import client from "app/client";
import { getCourtierInfo } from "services/authAPI";
import { getBase64 } from "helpers/getBase64";
import { useTranslation } from "react-i18next";
import { NsKey } from "enums/NsKey";
import { Button } from "components/ui/Button";
import { Sizes } from "enums/sizes";
import { Menu, MenuItems, MenuItem, MenuButton } from "@headlessui/react";

interface SendingEmailContentProps {
  devisId?: string;
  initBody?: string;
  emailTo?: string[] | string;
  docsToSend?: any[];
  object?: string;
  changeStatut?: boolean;
  changeActiveView: () => void;
  onClose: () => void;
}

const SendingEmailContent: FC<SendingEmailContentProps> = ({
  devisId,
  initBody = "",
  emailTo,
  docsToSend = [],
  object,
  changeStatut,
  changeActiveView,
  onClose,
}) => {
  const { t } = useTranslation(NsKey.Common);
  const [listDocumentsForAssure, setListDocumentsForAssure] = useState<any>([]);
  const [documentAdded, setDocumentAdded] = useState<any>([]);

  const [adressFrom, setAdressFrom] = useState("");
  const [subject, setSubject] = useState(object || "Devis");
  const [body, setBody] = useState(initBody || "");
  const [addresses, setAddresses] = useState<string[]>(
    Array.isArray(emailTo) ? emailTo : emailTo ? [emailTo] : []
  );
  const [inputValue, setInputValue] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAddresses(Array.isArray(emailTo) ? emailTo : emailTo ? [emailTo] : []);
  }, [emailTo]);

  useEffect(() => {
    if (devisId) {
      getAssureEmail();
    }
    getCourtierEmail();
    fetchData();
  }, [devisId]);

  const getAssureEmail = async () => {
    const response = await client.get(`/backoffice/devis/${devisId}`);
    const res = response?.data;
    if (res?.devisSouscripteurs?.length > 0) {
      setAddresses([
        res?.devisSouscripteurs[0]?.souscripteur?.personnePhysique?.email ||
        res?.devisSouscripteurs[0]?.souscripteur?.personneMorale?.contacts[0]?.email,
      ]);
    }
  };

  const getCourtierEmail = async () => {
    const courtierInfo = await getCourtierInfo('');
    setAdressFrom(courtierInfo?.personneMorale?.contacts[0]?.email);
  };

  const fetchData = async () => {
    if (devisId) {
      const response = await client.get(`/backoffice/devis/doc-signed?devisId=${devisId}`);
      setListDocumentsForAssure([...(response?.data || [])]);
    }
  };

  const handleAdressFromChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAdressFrom(event.target.value);
  };

  const handleAdressToChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      if (inputValue.trim() !== "") {
        setAddresses([...addresses, inputValue.trim()]);
        setInputValue("");
      }
    }

    if (event.key === "Backspace" && inputValue === "" && addresses.length > 0) {
      setAddresses(addresses.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim() !== "") {
      setAddresses([...addresses, inputValue.trim()]);
      setInputValue("");
    }
  };

  const removeAddress = (index: number) => {
    setAddresses(addresses.filter((_, i) => i !== index));
  };

  const handleSubjectChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSubject(event.target.value);
  };

  const handleBodyChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setBody(event.target.value);
  };

  const sendDevisClick = async () => {
    let storedIds: string[] = [];
    if (listDocumentsForAssure?.length < 1 && docsToSend) {
      docsToSend.forEach((currentDoc: any) => {
        storedIds.push(currentDoc?.storedFileId || currentDoc?.docSignedStoredId);
      });
    }

    for (let index = 0; index < documentAdded?.length; index++) {
      const element = documentAdded[index];

      const dataForm: any = new FormData();
      dataForm.append("content", element.target);

    }

    for (let index = 0; index < listDocumentsForAssure?.length; index++) {
      storedIds.push(listDocumentsForAssure[index]?.documentStoredId);
    }

  };

  const deleteDocumentAdded = (document: any) => {
    setDocumentAdded((prev: any) =>
      prev?.filter((currentDoc: any) => currentDoc != document)
    );
  };

  const handleClickUploadFile = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const url = await getBase64(file);
      const data = {
        name: file?.name || "",
        size: (file?.size || 0) / 1000,
        type: file?.type?.split("/")?.[1] || "",
        url: url,
        target: file,
      };
      setDocumentAdded((prev: any) => [...prev, data]);
    }
  };

  return (
    <>
      <div className="text-center flex flex-col gap-4">
        <div className="pad-b10 flex">
          <p className="font-16 lh-19 font-w700 mg-r15">{t("De")}</p>
          <input
            type="text"
            className="font-16 lh-19 font-w700 text-blue"
            value={adressFrom}
            onChange={handleAdressFromChange}
          />
        </div>
        <div className="pad-b10 d-flex">
          <p className="font-16 lh-19 font-w700 mg-r15">{t("Envoyer à")}</p>
          <div className="d-flex flex-wrap flex-1">
            {addresses.map((address, index) => (
              <span
                key={index}
                className="d-flex badge gris text-blue flex-ver-center tiny"
              >
                {address}{" "}
                <button
                  className="button-default text-dark font-16 font-w700"
                  onClick={() => removeAddress(index)}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              className="font-16 lh-19 font-w700 text-blue flex-1"
              value={inputValue}
              onChange={handleAdressToChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={t("Ajouter une adresse...")}
            />
          </div>
        </div>
        <div className="pad-b10 d-flex mb-4">
          <p className="font-16 lh-19 font-w700 mg-r15">{t("Objet")}</p>
          <input
            className="font-16 lh-19 font-w700 text-blue"
            value={subject}
            onChange={handleSubjectChange}
          />
        </div>
        <div className="text-left mb-2">
          <p className="font-16 lh-19 font-w700 mg-b15">{t("Corps du mail")}</p>
          <textarea
            className="w-100 border rounded-lg border-box"
            value={body}
            onChange={handleBodyChange}
          ></textarea>
        </div>
        <div className="d-flex mg-b10 flex-hor-start flex-wrap">
          {listDocumentsForAssure?.map((document: any, index: number) => (
            <div
              key={"listdoc-" + index}
              className="text-blue font-14 flex-direction-row d-flex flex-ver-center badge gris mg-b5 mg-r5 w-fit min-w80"
            >
              <p className="font-italic">
                {document?.document?.designation || document?.designation}
              </p>
            </div>
          ))}
          {docsToSend?.map((document: any, index: number) => (
            <div
              key={"docsigned-" + index}
              className="text-blue font-14 flex-direction-row d-flex flex-ver-center badge gris w-fit mg-b5 mg-r5 min-w80"
            >
              <p className="font-italic">{document?.designation}</p>
            </div>
          ))}
          {documentAdded?.map((document: any, index: number) => (
            <div
              key={"docadd-" + index}
              className="text-blue font-14 flex-direction-row d-flex flex-ver-center badge gris w-fit mg-b5 mg-r5 min-w80"
            >
              <p className="font-italic">{document?.name}</p>
              <p className="mg-l10" onClick={() => deleteDocumentAdded(document)}>
                X
              </p>
            </div>
          ))}
        </div>

        <div className="d-flex flex-hor-between flex-ver-end mt-8">
          <div>
            <Menu>
              <MenuButton as={Button} color="primaryLight" round >
                {t("Joindre un document")}
              </MenuButton>
              <MenuItems
                transition
                anchor="top"
                className="w-64 z-50 origin-top-left rounded-xl border bg-white p-1 text-sm/6 text-black transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
              >
                <MenuItem>
                  <button
                    onClick={() => {
                      changeActiveView();
                    }}
                    className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/10"
                  >
                    {t("Choisir un fichier existant")}
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={handleClickUploadFile}
                    className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-black/10"
                  >
                    {t("Charger depuis mon ordinateur")}
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
            <input
              key={"file"}
              type="file"
              hidden
              id={"upload"}
              ref={fileInputRef}
              onChange={(e) => handleUploadFile(e)}
            />
          </div>
          <Button
            color="primary"
            size={Sizes.lg}
            onClick={() => sendDevisClick()}
          >
            {t("Envoyer")}
          </Button>
        </div>
      </div>
    </>
  );
};

export default SendingEmailContent;
